import { ethers } from 'ethers';
import { getChainById } from '../config/chains';
import { submitSorobanContractTx } from './stellarTx';

/** Keys ethers uses on `TransactionRequest` / overrides — never valid Soroban contract args. */
const ETHERS_TX_OVERRIDE_KEYS = new Set([
    'accessList',
    'blockTag',
    'ccipReadEnabled',
    'chainId',
    'customData',
    'from',
    'gasLimit',
    'gasPrice',
    'maxFeePerGas',
    'maxPriorityFeePerGas',
    'nonce',
    'type',
    'value',
]);

function isLikelyEthersTxOverridesObject(value) {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
    if (value instanceof Uint8Array) return false;
    const keys = Object.keys(value);
    if (keys.length === 0) return true;
    return keys.every((k) => ETHERS_TX_OVERRIDE_KEYS.has(k));
}

/**
 * Helper function to check if an error is a gas-related error that can be fixed with manual gas limit
 */
export const isGasEstimationError = (error) => {
    const errorMessage = error?.message || '';
    const errorData = error?.data || {};
    const errorDataMessage = errorData?.message || '';
    const errorDataCode = errorData?.code;
    const combinedMessage = `${errorMessage} ${errorDataMessage}`.toLowerCase();
    
    return error.code === 'UNPREDICTABLE_GAS_LIMIT' || 
           error.code === -32000 ||
           error.code === -32603 || // Internal JSON-RPC error (often gas-related)
           errorDataCode === -32000 || // Nested error code (common in JSON-RPC errors)
           (errorMessage && (
               errorMessage.includes('UNPREDICTABLE_GAS_LIMIT') ||
               errorMessage.includes('cannot estimate gas') ||
               errorMessage.includes('gas required exceeds allowance') ||
               errorMessage.includes('insufficient gas') ||
               errorMessage.includes('data gas cost')
           )) ||
           (errorDataMessage && (
               errorDataMessage.includes('insufficient gas') ||
               errorDataMessage.includes('data gas cost') ||
               errorDataMessage.includes('gas required exceeds allowance') ||
               errorDataMessage.includes('cannot estimate gas')
           )) ||
           (error.error && error.error.message && (
               error.error.message.includes('gas required exceeds allowance') ||
               error.error.message.includes('cannot estimate gas') ||
               error.error.message.includes('insufficient gas') ||
               error.error.message.includes('data gas cost')
           )) ||
           combinedMessage.includes('insufficient gas') ||
           combinedMessage.includes('data gas cost');
};

/**
 * Helper function to get gas fee overrides with buffer to prevent "maxFeePerGas less than block base fee" errors
 */
export const getGasFeeOverrides = async (provider) => {
    try {
        if (provider) {
            const feeData = await provider.getFeeData();
            if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
                // Add 20% buffer to maxFeePerGas to account for base fee increases
                const bufferedMaxFee = feeData.maxFeePerGas.mul(120).div(100);
                return {
                    maxFeePerGas: bufferedMaxFee,
                    maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
                };
            }
        }
    } catch (feeError) {
        console.warn('Failed to get fee data, proceeding without gas overrides:', feeError);
    }
    return {};
};

/**
 * Lightweight helper to estimate a **max gas cost** for a transaction
 * without needing to construct the full contract call.
 *
 * This uses:
 * - the provided `manualGasLimit` (or a sane default)
 * - the current network fee data (`maxFeePerGas` / `gasPrice`)
 *
 * The result is suitable for UI "View breakdown" style displays where we
 * want to show users a realistic upper-bound on the network fee before
 * they actually submit the transaction.
 *
 * @param {import('ethers').providers.Provider} provider - Ethers provider
 * @param {number|import('ethers').BigNumber|string} manualGasLimit - Gas limit to use for the max-cost estimate
 * @returns {Promise<{
 *   gasLimit: string,
 *   maxFeePerGas: string | null,
 *   gasPrice: string | null,
 *   totalMaxFeeNative: string,
 *   nativeSymbol: string
 * }>}
 */
export const estimateMaxGasFee = async (provider, manualGasLimit) => {
    if (!provider) {
        throw new Error('Provider not available for gas estimation');
    }

    const feeData = await provider.getFeeData();
    const network = await provider.getNetwork();

    // Normalise chain id to number for lookup
    const rawChainId = network?.chainId;
    const numericChainId = rawChainId != null
        ? (typeof rawChainId === 'number'
            ? rawChainId
            : (typeof rawChainId === 'bigint'
                ? Number(rawChainId)
                : (String(rawChainId).startsWith('0x')
                    ? parseInt(String(rawChainId), 16)
                    : parseInt(String(rawChainId), 10))))
        : null;

    const chainConfig = numericChainId != null ? getChainById(numericChainId) : null;
    const nativeDecimals = chainConfig?.nativeCurrency?.decimals ?? 18;
    const gasFeeUnitDecimals = chainConfig?.gasFeeUnitDecimals ?? nativeDecimals;
    const nativeSymbol = chainConfig?.nativeCurrency?.symbol ?? 'ETH';

    const gasLimitBn = ethers.BigNumber.isBigNumber(manualGasLimit)
        ? manualGasLimit
        : ethers.BigNumber.from(manualGasLimit || 500000);

    // Prefer EIP-1559 style fees when available
    const maxFeePerGas = feeData.maxFeePerGas || null;
    const gasPrice = feeData.gasPrice || null;
    const pricePerUnit = maxFeePerGas || gasPrice;

    if (!pricePerUnit) {
        throw new Error('Unable to fetch gas price data for estimation');
    }

    const totalMaxFee = gasLimitBn.mul(pricePerUnit);
    const totalMaxFeeNative = ethers.utils.formatUnits(totalMaxFee, gasFeeUnitDecimals);

    return {
        gasLimit: gasLimitBn.toString(),
        maxFeePerGas: maxFeePerGas ? maxFeePerGas.toString() : null,
        gasPrice: gasPrice ? gasPrice.toString() : null,
        totalMaxFeeNative,
        nativeSymbol,
    };
};

/** Buffer applied to estimated gas (same as executeContractTxWithGasFallback) */
const GAS_BUFFER_PERCENT = 35;
/** Minimum gas limit (same as executeContractTxWithGasFallback) */
const MIN_GAS_LIMIT = 150000;

/**
 * Compute both the estimated gas and the final gas limit that executeContractTxWithGasFallback
 * would send to the wallet. This mirrors its exact logic so the breakdown matches MetaMask.
 *
 * MetaMask shows:
 *   "Network fee" ~= estimatedGas × likely/effective gas price
 *   "Max fee"      =  gasLimit × maxFeePerGas (worst-case fee)
 *
 * @param {import('ethers').providers.Provider} provider
 * @param {import('ethers').Contract|null} contract
 * @param {string|null} methodName
 * @param {Array} methodArgs
 * @param {number} manualGasLimit - Caller-provided fallback gas limit
 * @param {Object} txOverrides - Transaction overrides (value / maxFeePerGas etc.)
 * @returns {Promise<{estimatedGas: import('ethers').BigNumber, gasLimit: import('ethers').BigNumber}>}
 */
const computeEffectiveGasLimit = async (provider, contract, methodName, methodArgs, manualGasLimit, txOverrides) => {
    // --- Resolve block / chain gas cap (same logic as executeContractTxWithGasFallback) ---
    let blockGasLimit = null;
    if (provider) {
        let chainMaxGasLimit = null;
        try {
            const network = await provider.getNetwork();
            const raw = network?.chainId;
            const chainId = raw != null
                ? (typeof raw === 'number' ? raw : (typeof raw === 'bigint' ? Number(raw) : (String(raw).startsWith('0x') ? parseInt(String(raw), 16) : parseInt(String(raw), 10))))
                : null;
            const chain = chainId != null ? getChainById(chainId) : null;
            if (chain?.maxGasLimit != null) {
                chainMaxGasLimit = ethers.BigNumber.from(chain.maxGasLimit);
            }
        } catch (_) { /* ignore */ }
        try {
            const block = await provider.getBlock('latest');
            if (chainMaxGasLimit) {
                blockGasLimit = chainMaxGasLimit;
            } else if (block && block.gasLimit) {
                blockGasLimit = ethers.BigNumber.from(block.gasLimit).mul(95).div(100);
            }
        } catch (_) {
            if (chainMaxGasLimit) blockGasLimit = chainMaxGasLimit;
        }
    }

    const capGas = (value) => {
        if (!blockGasLimit) return value;
        const bn = ethers.BigNumber.isBigNumber(value) ? value : ethers.BigNumber.from(value);
        return bn.gt(blockGasLimit) ? blockGasLimit : bn;
    };

    // --- Try estimateGas (same as executeContractTxWithGasFallback) ---
    let estimatedGasRaw = null;
    if (contract && methodName && typeof contract.estimateGas[methodName] === 'function') {
        try {
            const args = methodArgs ? methodArgs.slice() : [];
            estimatedGasRaw = await contract.estimateGas[methodName](...args, txOverrides);
        } catch (_) {
            estimatedGasRaw = null;
        }
    }

    let estimatedGasBn;
    if (estimatedGasRaw) {
        // Estimation succeeded: use the raw estimate for "Network fee"
        estimatedGasBn = ethers.BigNumber.from(estimatedGasRaw);
        // Apply buffer, floor, cap for the actual gasLimit sent to wallet
        let gasLimit = estimatedGasBn.mul(100 + GAS_BUFFER_PERCENT).div(100);
        if (gasLimit.lt(MIN_GAS_LIMIT)) gasLimit = ethers.BigNumber.from(MIN_GAS_LIMIT);
        if (manualGasLimit != null && manualGasLimit > MIN_GAS_LIMIT) {
            const floor = ethers.BigNumber.from(manualGasLimit);
            if (gasLimit.lt(floor)) gasLimit = floor;
        }
        return { estimatedGas: estimatedGasBn, gasLimit: capGas(gasLimit) };
    } else {
        // Estimation failed (revert / dummy proof / no contract). Use manualGasLimit so we never
        // show or send a full block gas limit (e.g. 30M on Arbitrum would show ~2M ETH fee).
        // Cap at block limit only to stay within network rules.
        const manualBn = ethers.BigNumber.from(Math.max(manualGasLimit, MIN_GAS_LIMIT));
        const fallbackGasLimit = blockGasLimit && manualBn.gt(blockGasLimit)
            ? blockGasLimit
            : manualBn;
        return { estimatedGas: fallbackGasLimit, gasLimit: fallbackGasLimit };
    }
};

/**
 * Estimate transaction fees to match what the wallet (MetaMask) will show.
 *
 * The key insight: MetaMask computes fees from the transaction we send it:
 *   Network fee  ≈ estimatedGas × likely/effective gas price
 *   Max fee      = gasLimit × maxFeePerGas
 *
 * So we must use the SAME gasLimit and maxFeePerGas that executeContractTxWithGasFallback
 * would pass. This function mirrors that logic exactly.
 */
export const estimateTxFees = async (contract, methodName, methodArgs, provider, options = {}) => {
    const { manualGasLimit = 500000, txOverrides = {} } = options;

    if (!provider) {
        throw new Error('Provider not available for gas estimation');
    }

    const [feeData, gasOverrides, network] = await Promise.all([
        provider.getFeeData(),
        getGasFeeOverrides(provider),
        provider.getNetwork(),
    ]);
    let block = null;
    try {
        block = await provider.getBlock('latest');
    } catch (e) {
        console.warn('Could not fetch latest block for base fee:', e);
    }

    const rawChainId = network?.chainId;
    const numericChainId = rawChainId != null
        ? (typeof rawChainId === 'number'
            ? rawChainId
            : (typeof rawChainId === 'bigint'
                ? Number(rawChainId)
                : (String(rawChainId).startsWith('0x')
                    ? parseInt(String(rawChainId), 16)
                    : parseInt(String(rawChainId), 10))))
        : null;

    const chainConfig = numericChainId != null ? getChainById(numericChainId) : null;
    const nativeDecimals = chainConfig?.nativeCurrency?.decimals ?? 18;
    const gasFeeUnitDecimals = chainConfig?.gasFeeUnitDecimals ?? nativeDecimals;
    const nativeSymbol = chainConfig?.nativeCurrency?.symbol ?? 'ETH';

    const maxFeePerGasRaw = feeData.maxFeePerGas || null;
    const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas || null;
    const gasPrice = feeData.gasPrice || null;
    const baseFeePerGas = block?.baseFeePerGas ? block.baseFeePerGas.toString() : null;
    const estimateFromPriorityFee = !!chainConfig?.estimateNetworkFeeFromPriorityFee;

    // Match executeContractTxWithGasFallback merge behavior:
    // existing overrides first, then gasOverrides (buffered maxFeePerGas wins when available)
    const mergedTxOverrides = { ...txOverrides, ...gasOverrides };

    // The maxFeePerGas we actually send in the transaction
    const maxFeePerGasSent = mergedTxOverrides.maxFeePerGas || maxFeePerGasRaw || gasPrice;
    if (!maxFeePerGasSent) {
        throw new Error('Unable to fetch gas price data for estimation');
    }

    // Compute both estimatedGas (for "Network fee") and gasLimit (for "Max fee")
    // This matches what executeContractTxWithGasFallback would send to the wallet
    const { estimatedGas: estimatedGasBn, gasLimit: gasLimitBn } = await computeEffectiveGasLimit(
        provider, contract, methodName, methodArgs, manualGasLimit, mergedTxOverrides
    );

    let estimatedPricePerGas;
    if (estimateFromPriorityFee && maxPriorityFeePerGas) {
        // Non-standard fee-token chains (Arc/Stable/Tempo) align better with priority-fee UI pricing.
        estimatedPricePerGas = maxPriorityFeePerGas.gt(maxFeePerGasSent)
            ? maxFeePerGasSent
            : maxPriorityFeePerGas;
    } else {
        // Standard EIP-1559 behavior for ETH-like chains:
        // likely price ~= min(maxFeePerGas, baseFee + priorityFee)
        const baseFeeBn = block?.baseFeePerGas || feeData.lastBaseFeePerGas || null;
        if (baseFeeBn && maxPriorityFeePerGas) {
            const effective1559 = baseFeeBn.add(maxPriorityFeePerGas);
            estimatedPricePerGas = effective1559.gt(maxFeePerGasSent) ? maxFeePerGasSent : effective1559;
        } else if (gasPrice) {
            estimatedPricePerGas = gasPrice.gt(maxFeePerGasSent) ? maxFeePerGasSent : gasPrice;
        } else {
            estimatedPricePerGas = maxFeePerGasSent;
        }
    }

    // MetaMask-like:
    // "Network fee" ~= estimatedGas × likely/effective gas price
    // "Max fee"     =  gasLimit × maxFeePerGas
    const estimatedTotal = estimatedGasBn.mul(estimatedPricePerGas);
    const estimatedFeeNative = ethers.utils.formatUnits(estimatedTotal, gasFeeUnitDecimals);

    const maxTotal = gasLimitBn.mul(maxFeePerGasSent);
    const maxFeeNative = ethers.utils.formatUnits(maxTotal, gasFeeUnitDecimals);

    return {
        estimatedGasLimit: estimatedGasBn.toString(),
        gasLimitUsed: gasLimitBn.toString(),
        maxFeePerGas: maxFeePerGasSent ? maxFeePerGasSent.toString() : null,
        estimatedPricePerGas: estimatedPricePerGas ? estimatedPricePerGas.toString() : null,
        maxPriorityFeePerGas: maxPriorityFeePerGas ? maxPriorityFeePerGas.toString() : null,
        gasPrice: gasPrice ? gasPrice.toString() : null,
        baseFeePerGas,
        estimatedFeeNative,
        maxFeeNative,
        nativeSymbol,
        nativeDecimals,
        gasFeeUnitDecimals,
        lastUpdated: Date.now(),
    };
};

/**
 * Execute a contract transaction with gas estimation fallback
 * This handles gas estimation failures gracefully by using manual gas limits
 * 
 * @param {Object} contract - The ethers contract instance
 * @param {string} methodName - The contract method name (e.g., 'deposit', 'transferConfidential')
 * @param {Array} methodArgs - Array of arguments for the contract method
 * @param {Object} options - Options object
 * @param {number} options.manualGasLimit - Manual gas limit to use if estimation fails (default: 500000)
 * @param {Function} options.rateLimitRetry - Optional rate limit retry wrapper function
 * @param {Object} options.retryOptions - Options for rate limit retry (if provided)
 * @returns {Promise<ethers.ContractTransaction>} The transaction object
 */
export const executeContractTxWithGasFallback = async (
    contract,
    methodName,
    methodArgs,
    options = {}
) => {
    const {
        manualGasLimit = 500000,
        rateLimitRetry = null,
        retryOptions = null,
        stellarContext = null,
    } = options;

    if (stellarContext) {
        let sorobanArgs = methodArgs;
        if (methodArgs.length > 0) {
            const lastArg = methodArgs[methodArgs.length - 1];
            // EVM callers append `{ value?, maxFeePerGas?, ... }` as the last arg; Soroban must not
            // forward that (e.g. transferConfidential needs exactly 4 args after invoker prepend).
            if (isLikelyEthersTxOverridesObject(lastArg)) {
                sorobanArgs = methodArgs.slice(0, -1);
            }
        }
        return submitSorobanContractTx(methodName, sorobanArgs, stellarContext);
    }

    // Get gas fee overrides
    const provider = contract.signer?.provider;
    const gasOverrides = await getGasFeeOverrides(provider);

    // Check if the last argument is an overrides object (for methods like transferConfidential)
    // If so, extract it and merge with gas overrides
    let existingOverrides = {};
    let argsForEstimation = methodArgs;
    let argsForExecution = methodArgs;
    
    if (methodArgs.length > 0) {
        const lastArg = methodArgs[methodArgs.length - 1];
        // Check if last argument is an object with transaction properties (not a string/number/boolean)
        if (typeof lastArg === 'object' && lastArg !== null && !Array.isArray(lastArg) &&
            (lastArg.hasOwnProperty('value') || lastArg.hasOwnProperty('gasLimit') || 
             lastArg.hasOwnProperty('maxFeePerGas') || lastArg.hasOwnProperty('gasPrice'))) {
            existingOverrides = lastArg;
            // Remove the last argument for estimation and execution, we'll add merged overrides
            argsForEstimation = methodArgs.slice(0, -1);
            argsForExecution = methodArgs.slice(0, -1);
        }
    }

    // Merge existing overrides with gas overrides
    const mergedOverrides = { ...existingOverrides, ...gasOverrides };

    // Helper to execute the transaction
    const executeTx = async (overrides) => {
        const finalArgs = [...argsForExecution, overrides];
        const txPromise = () => contract[methodName](...finalArgs);
        if (rateLimitRetry && retryOptions) {
            return await rateLimitRetry(txPromise, retryOptions);
        }
        return await txPromise();
    };

    // Minimum gas limit to account for data gas costs (EIP-4844) and floor costs
    // This is especially important for networks like Base
    const MIN_GAS_LIMIT = 150000; // Minimum to cover data gas floor costs

    // Cap gas limit to block gas limit (and chain-specific max) to avoid "gas limit too high" errors
    let blockGasLimit = null;
    if (provider) {
        // Resolve chain first so we always apply chain-specific max (e.g. Tempo) even if getBlock fails
        let chainMaxGasLimit = null;
        try {
            const network = await provider.getNetwork();
            const raw = network?.chainId;
            const chainId = raw != null
                ? (typeof raw === 'number' ? raw : (typeof raw === 'bigint' ? Number(raw) : (String(raw).startsWith('0x') ? parseInt(String(raw), 16) : parseInt(String(raw), 10))))
                : null;
            const chain = chainId != null ? getChainById(chainId) : null;
            if (chain?.maxGasLimit != null) {
                chainMaxGasLimit = ethers.BigNumber.from(chain.maxGasLimit);
            }
        } catch (e) {
            console.warn('Could not resolve chain for gas cap:', e);
        }
        try {
            const block = await provider.getBlock('latest');
            if (chainMaxGasLimit) {
                // Chain has a known per-tx gas cap (e.g. Tempo 16.5M); use it so we don't cap to a low block limit
                blockGasLimit = chainMaxGasLimit;
            } else if (block && block.gasLimit) {
                blockGasLimit = ethers.BigNumber.from(block.gasLimit).mul(95).div(100);
            }
        } catch (e) {
            console.warn('Could not fetch block gas limit:', e);
            if (chainMaxGasLimit) blockGasLimit = chainMaxGasLimit;
        }
    }
    const capGas = (value) => {
        if (!blockGasLimit) return value;
        const bn = ethers.BigNumber.isBigNumber(value) ? value : ethers.BigNumber.from(value);
        if (bn.gt(blockGasLimit)) {
            console.warn(`Capping gas limit from ${bn.toString()} to block limit ${blockGasLimit.toString()} to avoid "gas limit too high"`);
            return blockGasLimit;
        }
        return bn;
    };
    
    // Smart gas: use estimated gas + buffer, capped at network max; when estimation fails, use full allowed amount
    const GAS_BUFFER_PERCENT = 35; // 35% buffer over estimate to avoid "out of gas"

    let tx;
    try {
        const estimateMethod = contract.estimateGas[methodName];
        const estimatedGas = await estimateMethod(...argsForEstimation, mergedOverrides);

        // Use estimate + buffer, at least minimum, then cap at network max (never too high)
        let gasLimit = estimatedGas.mul(100 + GAS_BUFFER_PERCENT).div(100);
        if (gasLimit.lt(MIN_GAS_LIMIT)) gasLimit = ethers.BigNumber.from(MIN_GAS_LIMIT);
        // When caller provides manualGasLimit (e.g. for heavy ops like withdraw), use it as a floor so chains
        // where estimation under-reports (e.g. Tempo) still get enough gas
        if (manualGasLimit != null && manualGasLimit > MIN_GAS_LIMIT) {
            const floor = ethers.BigNumber.from(manualGasLimit);
            if (gasLimit.lt(floor)) gasLimit = floor;
        }
        gasLimit = capGas(gasLimit);

        const overridesWithGas = { ...mergedOverrides, gasLimit };
        tx = await executeTx(overridesWithGas);
    } catch (estimateError) {
        if (isGasEstimationError(estimateError)) {
            console.warn(`Gas estimation failed for ${methodName}, using fallback gas limit:`, estimateError);

            // Use manual gas limit when we can't estimate (e.g. dummy proof). Only cap at block
            // limit if manual exceeds it; never use full block limit for a single tx.
            let effectiveManualGasLimit = Math.max(manualGasLimit, MIN_GAS_LIMIT);
            if (blockGasLimit) {
                const blockNum = ethers.BigNumber.isBigNumber(blockGasLimit) ? blockGasLimit.toNumber() : Number(blockGasLimit);
                effectiveManualGasLimit = Math.min(effectiveManualGasLimit, blockNum);
            }
            console.log(`Using manual gas limit for ${methodName}:`, effectiveManualGasLimit);

            const overridesWithManualGas = { ...mergedOverrides, gasLimit: effectiveManualGasLimit };
            
            // Try the transaction with manual gas limit
            try {
                tx = await executeTx(overridesWithManualGas);
            } catch (txError) {
                // If the transaction call itself fails with a gas error, it might be trying to estimate again
                // In this case, we need to use the contract's populateTransaction method and send it directly
                if (isGasEstimationError(txError)) {
                    console.warn(`Transaction call also failed with gas error for ${methodName}, using populateTransaction:`, txError);
                    
                    // Use populateTransaction to build the transaction without estimation, then send it
                    const populatedTx = await contract.populateTransaction[methodName](...argsForEstimation, mergedOverrides);
                    // Use the same effective manual gas limit we calculated earlier
                    populatedTx.gasLimit = effectiveManualGasLimit;
                    if (overridesWithManualGas.maxFeePerGas) {
                        populatedTx.maxFeePerGas = overridesWithManualGas.maxFeePerGas;
                    }
                    if (overridesWithManualGas.maxPriorityFeePerGas) {
                        populatedTx.maxPriorityFeePerGas = overridesWithManualGas.maxPriorityFeePerGas;
                    }
                    if (overridesWithManualGas.value) {
                        populatedTx.value = overridesWithManualGas.value;
                    }
                    tx = await contract.signer.sendTransaction(populatedTx);
                } else {
                    throw txError;
                }
            }
        } else {
            // For other errors, re-throw them as they indicate actual transaction failures
            throw estimateError;
        }
    }

    return tx;
};
